"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Skeleton } from "@/components/ui/skeleton";
import { useCashDetails } from "@/lib/queries/payment";
import { ErrorBanner } from "./error-banner";
import { InstructionContainer } from "./instruction-container";

const toImageSrc = (image: string) => {
  if (image.startsWith("data:") || image.startsWith("http")) return image;
  return `data:image/png;base64,${image}`;
};

export const CashInstructions = () => {
  const t = useTranslations("instructions");
  const { data, isPending, isError, refetch } = useCashDetails();

  const renderDetails = () => {
    if (isPending)
      return (
        <div className="flex w-full flex-col items-center space-y-1">
          <Skeleton className="h-5 w-[200px] rounded-lg" />
          <Skeleton className="h-36 w-[272px] rounded-lg" />
        </div>
      );
    if (isError) return null;

    return (
      <div className="flex w-full flex-col items-center space-y-1">
        <p className="font-semibold text-sm tabular-nums">{data.reference}</p>
        {data.image && (
          <div className="rounded-lg bg-white p-2">
            <Image
              width={256}
              height={128}
              className="mx-auto max-h-32"
              src={toImageSrc(data.image)}
              alt={t("cash.barcodeAlt")}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {isError && <ErrorBanner refetch={refetch} />}
      <InstructionContainer isError={isError}>
        <div>
          {t.rich("cash.intro", {
            link: (chunks) => (
              <a
                href="https://docs.google.com/spreadsheets/d/1Rlwe3cBUBKlDIOOU2aIXusxRxc499bXfDOiruMTwVtc/edit?usp=sharing"
                rel="noopener noreferrer"
                target="_blank"
                className="text-accent"
              >
                {chunks}
              </a>
            ),
          })}
        </div>
        {renderDetails()}
        <div>
          <p>{t("cash.step1")}</p>
          <p>{t("cash.step2")}</p>
          <p>{t("cash.step3")}</p>
          <p>{t("cash.step4")}</p>
        </div>
        <p className="text-muted text-sm">
          {t.rich("help", {
            email: (chunks) => (
              <a className="text-accent" href="mailto:info@paycode.com.mx">
                {chunks}
              </a>
            ),
            phone: (chunks) => (
              <a className="text-accent" href="tel:+525624870068">
                {chunks}
              </a>
            ),
          })}
        </p>
      </InstructionContainer>
    </div>
  );
};
